import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { IEntity } from '@/Module/Core/Model/DataModel';
import type { DeviceType } from '@/Module/Core/Model/Enum';
import type { IMonthQuery } from '@/Module/Workspace/Model/ApiModel';
import type { IBookmark, ILike, IRecentlyViewedVideo, IStatistics } from '@/Module/Workspace/Model/DataModel';
import {
  getBookmarkListAction,
  getLikeListAction,
  getRecentlyViewedVideosAction,
  getStatisticsAction,
} from '@/Module/Workspace/Store/action';

const WorkspaceSlice = createSlice({
  name: 'workspace',
  initialState: {
    statistics: {
      data: [] as IStatistics[] | [],
      isLoading: false,
    },
    likes: {
      data: [] as ILike[] | [],
      isLoading: false,
      PageNo: 1,
      total: 0,
    },
    bookmarks: {
      data: [] as IBookmark[] | [],
      isLoading: false,
      PageNo: 1,
      total: 0,
    },
    recentlyViewed: {
      data: [] as IRecentlyViewedVideo[],
      selectedGradeId: '' as IEntity['id'],
      selectedCourseId: '' as IEntity['id'],
      selectedCategoryId: '' as IEntity['id'],
      isLoading: false,
    },
    selectedDate: 0 as IMonthQuery['month'],
    selectedGradeId: '5' as IEntity['id'],
    deviceType: null as DeviceType | null,
  },
  reducers: {
    monthChangeHandler: (state, { payload }: PayloadAction<IMonthQuery['month']>) => {
      state.selectedDate = payload;
    },
    setGradeIdRecent: (state, { payload }: PayloadAction<IEntity['id']>) => {
      state.recentlyViewed.selectedGradeId = payload;
    },
    setCategoryId: (state, { payload }: PayloadAction<IEntity['id']>) => {
      state.recentlyViewed.selectedCategoryId = payload;
    },
    setCourseId: (state, { payload }: PayloadAction<IEntity['id']>) => {
      state.recentlyViewed.selectedCourseId = payload;
    },
    bookmarkGoToPage: (state, { payload }: PayloadAction<number>) => {
      state.bookmarks.PageNo = payload;
    },
    likesGoToPage: (state, { payload }: PayloadAction<number>) => {
      state.likes.PageNo = payload;
    },
    setDeviceType: (state, { payload }: PayloadAction<DeviceType>) => {
      state.deviceType = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getStatisticsAction.pending, state => {
        state.statistics.isLoading = true;
      })
      .addCase(getStatisticsAction.rejected, state => {
        state.statistics.isLoading = false;
      })
      .addCase(getStatisticsAction.fulfilled, (state, { payload }) => {
        state.statistics.data = payload;
        state.statistics.isLoading = false;
      });
    builder
      .addCase(getRecentlyViewedVideosAction.pending, state => {
        state.recentlyViewed.isLoading = true;
      })
      .addCase(getRecentlyViewedVideosAction.rejected, state => {
        state.recentlyViewed.isLoading = false;
      })
      .addCase(getRecentlyViewedVideosAction.fulfilled, (state, { payload }) => {
        state.recentlyViewed.data = payload;
        state.recentlyViewed.isLoading = false;
      });
    builder
      .addCase(getBookmarkListAction.pending, state => {
        state.bookmarks.isLoading = true;
      })
      .addCase(getBookmarkListAction.rejected, state => {
        state.bookmarks.isLoading = false;
      })
      .addCase(getBookmarkListAction.fulfilled, (state, { payload }) => {
        if (state.bookmarks.data && state.deviceType === 'MOBILE') {
          state.bookmarks.data = [...state.bookmarks.data, ...payload.data];
        } else {
          state.bookmarks.data = payload.data;
        }
        state.bookmarks.isLoading = false;
        state.bookmarks.total = payload.total;
      });
    builder
      .addCase(getLikeListAction.pending, state => {
        state.likes.isLoading = true;
      })
      .addCase(getLikeListAction.rejected, state => {
        state.likes.isLoading = false;
      })
      .addCase(getLikeListAction.fulfilled, (state, { payload }) => {
        if (state.likes.data && state.deviceType === 'MOBILE') {
          state.likes.data = [...state.likes.data, ...payload.data];
        } else {
          state.likes.data = payload.data;
        }

        state.likes.isLoading = false;
        state.likes.total = payload.total;
      });
  },
});

export const {
  monthChangeHandler,
  setGradeIdRecent,
  setCategoryId,
  setCourseId,
  bookmarkGoToPage,
  likesGoToPage,
  setDeviceType,
} = WorkspaceSlice.actions;

export default WorkspaceSlice;
